import { useMemo } from "react";
import sleeperPlayersSnapshot from "../data/sleeperPlayers.json";
import type { Player } from "../models/Player";
import { normalizeName } from "../utils/espn";
import { jaroWinkler } from "../utils/nameMatch";
import { formatPlayerHeight } from "../utils/playerHeight";
import { formatTeamAbbreviation } from "../utils/teamAbbreviation";

type ResolverInput = {
  id?: string;
  name?: string;
  team?: string;
  position?: string;
};

type SleeperPlayersSnapshot = {
  generatedAt?: string;
  players?: Player[];
};

const FIRST_NAME_ALIASES: Record<string, string[]> = {
  chig: ["chigoziem"],
  chigoziem: ["chig"],
  hollywood: ["marquise"],
  marquise: ["hollywood"],
  gabe: ["gabriel"],
  gabriel: ["gabe"],
  mike: ["michael"],
  michael: ["mike"],
  chris: ["christopher"],
  christopher: ["chris"],
  pat: ["patrick"],
  patrick: ["pat"],
};

function normalizeTeam(team: unknown) {
  return formatTeamAbbreviation(team).replace(/[^A-Z]/g, "").trim();
}

function tokenizeNormName(value: string | undefined) {
  return normalizeName(String(value ?? ""))
    .split(" ")
    .filter(Boolean);
}

function firstNamesAlign(left: string, right: string) {
  if (!left || !right) return false;
  return (
    left === right ||
    left[0] === right[0] ||
    FIRST_NAME_ALIASES[left]?.includes(right) ||
    FIRST_NAME_ALIASES[right]?.includes(left)
  );
}

function getNameSimilarity(candidate: Player, input: ResolverInput) {
  const candidateNorm = normalizeName(candidate.name);
  const inputNorm = normalizeName(String(input.name ?? ""));
  if (!candidateNorm || !inputNorm) return 0;
  if (candidateNorm === inputNorm) return 1;

  const candidateTokens = tokenizeNormName(candidate.name);
  const inputTokens = tokenizeNormName(input.name);
  const candidateLast = candidateTokens[candidateTokens.length - 1] ?? "";
  const inputLast = inputTokens[inputTokens.length - 1] ?? "";
  const candidateFirst = candidateTokens[0] ?? "";
  const inputFirst = inputTokens[0] ?? "";
  const baseScore = jaroWinkler(candidateNorm, inputNorm);

  if (candidateLast && inputLast && candidateLast === inputLast && firstNamesAlign(candidateFirst, inputFirst)) {
    return Math.max(baseScore, 0.84);
  }

  return baseScore;
}

function getLastToken(value: string | undefined) {
  const tokens = tokenizeNormName(value);
  return tokens[tokens.length - 1] ?? "";
}

function chooseBestCandidate(candidates: Player[], input: ResolverInput) {
  const inputTeam = normalizeTeam(input.team);
  const inputPos = String(input.position ?? "").toUpperCase().trim();

  let best: Player | undefined;
  let bestScore = Number.NEGATIVE_INFINITY;

  candidates.forEach((candidate) => {
    const nameScore = getNameSimilarity(candidate, input);
    let score = nameScore * 10;
    const candidateTeam = normalizeTeam(candidate.team);
    const candidatePos = String(candidate.position ?? "").toUpperCase().trim();

    if (inputTeam && candidateTeam) score += inputTeam === candidateTeam ? 3 : -1;
    if (inputPos && candidatePos) score += inputPos === candidatePos ? 2 : -2;

    if (!best || score > bestScore) {
      best = candidate;
      bestScore = score;
    }
  });

  return best;
}

function isAcceptableCloseMatch(candidate: Player, input: ResolverInput) {
  const nameScore = getNameSimilarity(candidate, input);
  if (nameScore >= 0.88) return true;

  const candidateLast = getLastToken(candidate.name);
  const inputLast = getLastToken(input.name);
  if (!candidateLast || !inputLast || candidateLast !== inputLast) return false;

  const candidateFirst = tokenizeNormName(candidate.name)[0] ?? "";
  const inputFirst = tokenizeNormName(input.name)[0] ?? "";
  if (!firstNamesAlign(candidateFirst, inputFirst)) return false;

  const candidateTeam = normalizeTeam(candidate.team);
  const inputTeam = normalizeTeam(input.team);
  const candidatePos = String(candidate.position ?? "").toUpperCase().trim();
  const inputPos = String(input.position ?? "").toUpperCase().trim();
  const teamMatches = Boolean(candidateTeam && inputTeam && candidateTeam === inputTeam);
  const positionMatches = Boolean(candidatePos && inputPos && candidatePos === inputPos);

  if (teamMatches && positionMatches && nameScore >= 0.55) return true;
  if (positionMatches && nameScore >= 0.78) return true;
  return false;
}

function resolveCanonicalIdFromIndexes(
  input: ResolverInput,
  indexes: {
    sleeperById: Map<string, Player>;
    byExactNormName: Map<string, Player[]>;
    byLastToken: Map<string, Player[]>;
    legacyIdMap: Map<string, string>;
  }
) {
  const rawId = String(input.id ?? "").trim();
  if (rawId) {
    if (indexes.sleeperById.has(rawId)) return rawId;
    const mappedLegacyId = indexes.legacyIdMap.get(rawId);
    if (mappedLegacyId) return mappedLegacyId;
  }

  const normName = normalizeName(String(input.name ?? ""));
  if (!normName) return undefined;

  const exact = indexes.byExactNormName.get(normName);
  if (exact?.length) return chooseBestCandidate(exact, input)?.id;

  const sameLastNameCandidates = indexes.byLastToken.get(getLastToken(input.name)) ?? [];
  const closeCandidates = sameLastNameCandidates.filter((candidate) => isAcceptableCloseMatch(candidate, input));
  if (closeCandidates.length) return chooseBestCandidate(closeCandidates, input)?.id;

  return undefined;
}

function buildResolverIndexes(sleeperPlayers: Player[], legacyPlayers: Player[]) {
  const sleeperById = new Map<string, Player>();
  const byExactNormName = new Map<string, Player[]>();
  const byLastToken = new Map<string, Player[]>();

  sleeperPlayers.forEach((player) => {
    sleeperById.set(player.id, player);
    const norm = normalizeName(player.name);
    if (!norm) return;
    const list = byExactNormName.get(norm) ?? [];
    list.push(player);
    byExactNormName.set(norm, list);

    const lastToken = getLastToken(player.name);
    if (lastToken) {
      const sameLastName = byLastToken.get(lastToken) ?? [];
      sameLastName.push(player);
      byLastToken.set(lastToken, sameLastName);
    }
  });

  const legacyIdMap = new Map<string, string>();
  legacyPlayers.forEach((legacyPlayer) => {
    const resolved = resolveCanonicalIdFromIndexes(
      {
        id: legacyPlayer.id,
        name: legacyPlayer.name,
        team: legacyPlayer.team,
        position: legacyPlayer.position,
      },
      { sleeperById, byExactNormName, byLastToken, legacyIdMap }
    );
    if (resolved) legacyIdMap.set(legacyPlayer.id, resolved);
  });

  return { sleeperById, byExactNormName, byLastToken, legacyIdMap };
}

export function useSleeperDirectory(args?: { legacyPlayers?: Player[] }) {
  const legacyPlayers = useMemo(() => args?.legacyPlayers ?? [], [args?.legacyPlayers]);
  const snapshot = useMemo(() => sleeperPlayersSnapshot as unknown as SleeperPlayersSnapshot, []);

  const sleeperPlayers = useMemo(() => {
    return Array.isArray(snapshot.players)
      ? snapshot.players.map((player) => ({
          ...player,
          height: formatPlayerHeight(player.height),
        }))
      : [];
  }, [snapshot]);

  const indexes = useMemo(() => buildResolverIndexes(sleeperPlayers, legacyPlayers), [legacyPlayers, sleeperPlayers]);

  const resolveCanonicalId = useMemo(() => {
    return (input: ResolverInput) => resolveCanonicalIdFromIndexes(input, indexes);
  }, [indexes]);

  const canonicalizePlayer = useMemo(() => {
    return (player: Player): Player => {
      const canonicalId = resolveCanonicalId({
        id: player.id,
        name: player.name,
        team: player.team,
        position: player.position,
      });

      return canonicalId ? { ...player, id: canonicalId } : player;
    };
  }, [resolveCanonicalId]);

  const canonicalizePlayers = useMemo(() => {
    return (players: Player[]) => {
      const mergedById = new Map<string, Player>();

      for (const player of players) {
        const canonical = canonicalizePlayer(player);
        const existing = mergedById.get(canonical.id);
        mergedById.set(canonical.id, { ...existing, ...canonical });
      }

      return Array.from(mergedById.values());
    };
  }, [canonicalizePlayer]);

  const canonicalizeRankingIds = useMemo(() => {
    return (rankingIds: string[], sourcePlayers: Player[]) => {
      const sourceById = new Map(sourcePlayers.map((player) => [player.id, player]));
      const nextIds: string[] = [];
      const seen = new Set<string>();

      rankingIds.forEach((id) => {
        const sourcePlayer = sourceById.get(id);
        const canonicalId = resolveCanonicalId({
          id,
          name: sourcePlayer?.name,
          team: sourcePlayer?.team,
          position: sourcePlayer?.position,
        }) ?? sourcePlayer?.id ?? id;

        if (!canonicalId || seen.has(canonicalId)) return;
        seen.add(canonicalId);
        nextIds.push(canonicalId);
      });

      return nextIds;
    };
  }, [resolveCanonicalId]);

  return {
    sleeperPlayers,
    resolveCanonicalId,
    canonicalizePlayer,
    canonicalizePlayers,
    canonicalizeRankingIds,
  };
}
